/**
 * Essay Gate Component
 * Full essay writing exercise with vocabulary analysis feedback
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EssayQuestion, GateEvaluationResult, VocabularyAnalysis } from '../../types/writing';
import { ClickableText } from '../ClickableWord';
import FeedbackModal from './FeedbackModal';
import './GateComponents.css';

interface EssayGateProps {
  question: EssayQuestion;
  onSubmit: (answer: string) => void;
  submitting: boolean;
  result: GateEvaluationResult | null;
  vocabularyAnalysis?: VocabularyAnalysis;
  onSaveToNotes?: () => void;
  onSaveItemToNotes?: (item: string) => void;
  onFamiliarityUpdate?: () => void;
}

const EssayGate: React.FC<EssayGateProps> = ({
  question,
  onSubmit,
  submitting,
  result,
  vocabularyAnalysis,
  onSaveToNotes,
  onSaveItemToNotes,
  onFamiliarityUpdate,
}) => {
  const { t } = useTranslation(['writing', 'common']);
  const [answer, setAnswer] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Reset answer when question changes
  useEffect(() => {
    setAnswer('');
  }, [question.id]);

  // Show feedback modal when result is first received
  useEffect(() => {
    if (result) {
      setShowFeedbackModal(true);
    }
  }, [result]);

  // Word count
  const wordCount = useMemo(() => {
    if (!answer.trim()) return 0;
    return answer.trim().split(/\s+/).length;
  }, [answer]);

  const withinWordLimit = wordCount >= question.minWords && wordCount <= question.maxWords;
  const tooShort = wordCount < question.minWords;
  const tooLong = wordCount > question.maxWords;

  const handleSubmit = () => {
    if (answer.trim() && !submitting) {
      onSubmit(answer.trim());
    }
  };

  return (
    <div className="gate-question essay-gate">
      {/* Question */}
      <div className="gate-question__card">
        <div className="gate-question__header">
          <span className="gate-question__type">{t('topic')}: <ClickableText text={question.topic} /></span>
        </div>

        <div className="gate-question__content">

          {/* Instructions */}
          <div className="essay-instructions">
            <p><ClickableText text={question.instructions} /></p>
          </div>

          {/* Key points to address */}
          {question.keyPoints && question.keyPoints.length > 0 && (
            <div className="essay-keypoints">
              <span className="essay-keypoints__label">{t('keyPointsToAddress')}:</span>
              <ul className="essay-keypoints__list">
                {question.keyPoints.map((point, index) => (
                  <li key={index}><ClickableText text={point} /></li>
                ))}
              </ul>
            </div>
          )}

          {/* Word limit */}
          <div className="essay-limit">
            <span className="essay-limit__label">{t('wordLimit')}:</span>
            <span className="essay-limit__value">
              {question.minWords} - {question.maxWords} {t('words')}
            </span>
          </div>

          {question.translation && (
            <p className="gate-question__translation">{question.translation}</p>
          )}
        </div>

        {/* Answer input */}
        {!result && (
          <div className="essay-input-container">
            <textarea
              className="essay-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t('writeYourEssay')}
              disabled={submitting}
              rows={12}
              autoFocus
            />
            <div className="essay-input-stats">
              <span className={`essay-word-count ${tooShort ? 'too-short' : tooLong ? 'too-long' : withinWordLimit ? 'ok' : ''}`}>
                {wordCount} / {question.minWords}-{question.maxWords} {t('words')}
                {tooShort && <span className="essay-word-hint"> ({t('needMore')})</span>}
                {tooLong && <span className="essay-word-hint"> ({t('tooMany')})</span>}
              </span>
            </div>
          </div>
        )}

        {/* Submitted answer display */}
        {result && (
          <div className="essay-submitted">
            <span className="essay-submitted__label">{t('yourEssay')}:</span>
            <div className="essay-submitted__text">{answer}</div>
            <span className="essay-submitted__wordcount">
              {wordCount} {t('words')}
            </span>
          </div>
        )}
      </div>

      {/* Result Summary (compact view) */}
      {result && (
        <div className={`gate-feedback gate-feedback--compact ${result.passed ? 'pass' : 'fail'}`}>
          <div className="gate-feedback__header">
            <span className="gate-feedback__status">
              {result.passed ? '✓ ' + t('wellDone') : '✗ ' + t('needsImprovement')}
            </span>
            <span className="gate-feedback__score">{result.score}%</span>
          </div>
          <p className="gate-feedback__text"><ClickableText text={result.feedback} /></p>

          {/* Vocabulary progress summary */}
          {vocabularyAnalysis && vocabularyAnalysis.totalFamiliarityGain > 0 && (
            <div className="gate-feedback__vocab-summary">
              <span className="vocab-progress-badge">
                +{vocabularyAnalysis.totalFamiliarityGain} {t('familiarityPoints')}
              </span>
              {vocabularyAnalysis.familiarityChanges.filter(c => c.isNew).length > 0 && (
                <span className="new-words-badge">
                  {vocabularyAnalysis.familiarityChanges.filter(c => c.isNew).length} {t('newWordsLearned')}
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="gate-feedback__btn-row">
            <button
              className="gate-feedback__view-details-btn"
              onClick={() => setShowFeedbackModal(true)}
            >
              {t('viewDetailedFeedback')}
            </button>
            {onSaveToNotes && (
              <button className="gate-feedback__save-note-btn" onClick={onSaveToNotes}>
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>

          {/* Quick actions */}
          <div className="gate-feedback__actions">
            {result.grammarIssues && result.grammarIssues.length > 0 && (
              <span className="action-badge">{result.grammarIssues.length} {t('grammarIssues')}</span>
            )}
            {result.suggestions && result.suggestions.length > 0 && (
              <span className="action-badge">{result.suggestions.length} {t('suggestions')}</span>
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      {!result && (
        <button
          className="gate-submit-btn"
          onClick={handleSubmit}
          disabled={!answer.trim() || submitting}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      )}

      {/* Feedback Modal */}
      {result && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          result={result}
          vocabularyAnalysis={vocabularyAnalysis}
          onSaveToNotes={onSaveItemToNotes}
          onFamiliarityUpdateComplete={onFamiliarityUpdate}
        />
      )}
    </div>
  );
};

export default EssayGate;
